(() => {
  const root = document.getElementById('PartnersWhatsApp');
  if (!root || window.__partnersJoyDrawerFixReady) return;
  window.__partnersJoyDrawerFixReady = true;

  const JOY_SIGNAL = /joy|avada|loyalty|reward/i;
  const markedSurfaceSelector = '[data-partners-hidden-rewards-launcher="true"]';
  let observerFrame = 0;

  /* Joy's own Rewards button stays visible on mobile (restored 21 Sep 2026).
     Desktop keeps it hidden because the dock already shows a Rewards button. */
  const keepNativeJoy = () => window.matchMedia('(max-width: 767px)').matches;

  const showNativeLauncher = (launcher) => {
    if (launcher.dataset.partnersJoyNativeLauncher !== 'true') return;
    delete launcher.dataset.partnersJoyNativeLauncher;
    launcher.style.removeProperty('opacity');
    launcher.style.removeProperty('pointer-events');
    launcher.style.removeProperty('user-select');
    launcher.removeAttribute('aria-hidden');
    launcher.removeAttribute('tabindex');
  };

  const closeHelpMenu = () => {
    const menu = document.getElementById('PartnersHelpMenu');
    const launcher = root.querySelector('[data-partners-hub-toggle]');

    if (menu) menu.hidden = true;
    if (launcher) launcher.setAttribute('aria-expanded', 'false');
  };

  const restoreJoySurface = () => {
    document.querySelectorAll(markedSurfaceSelector).forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      element.style.removeProperty('display');
      element.removeAttribute('hidden');
    });
  };

  const elementSignal = (element) => {
    let current = element;
    let signal = '';
    let depth = 0;

    while (current instanceof Element && depth < 4) {
      signal += ` ${current.id || ''} ${String(current.className || '')}`;
      signal += ` ${current.getAttribute('aria-label') || ''}`;
      signal += ` ${current.getAttribute('title') || ''}`;
      signal += ` ${current.getAttribute('data-testid') || ''}`;
      signal += ` ${current.getAttribute('data-widget') || ''}`;
      current = current.parentElement;
      depth += 1;
    }

    return signal;
  };

  const getFixedAncestor = (element) => {
    let current = element;
    let depth = 0;

    while (current instanceof HTMLElement && depth < 5) {
      const position = window.getComputedStyle(current).position;
      if (position === 'fixed' || position === 'sticky') return current;
      current = current.parentElement;
      depth += 1;
    }

    return null;
  };

  const hasFixedAncestor = (element) => !!getFixedAncestor(element);

  const isLauncherSized = (rect) => (
    rect.width >= 30 && rect.width <= 180 &&
    rect.height >= 30 && rect.height <= 180
  );

  const isNearBottomRight = (rect) => (
    rect.right >= window.innerWidth - 280 &&
    rect.bottom >= window.innerHeight - 300
  );

  /* On mobile Joy pins its launcher mid-screen on the right edge, above the
     bottom-right zone, so it was never hidden there. Accept that position too,
     but only when the launcher's own fixed wrapper is launcher-sized, so buttons
     inside the open Joy panel (a large fixed surface) can never qualify. */
  const isFloatingOnRightEdge = (element, rect) => {
    if (rect.right < window.innerWidth - 280) return false;
    if (rect.top < window.innerHeight * 0.25) return false;

    const fixedBox = getFixedAncestor(element);
    if (!fixedBox) return false;

    const box = fixedBox.getBoundingClientRect();
    return box.width <= 260 && box.height <= 200;
  };

  const candidateElements = () => {
    const candidates = new Set();

    document.querySelectorAll(markedSurfaceSelector).forEach((surface) => {
      if (!(surface instanceof Element)) return;
      if (surface.matches('button, a, [role="button"], [onclick]')) candidates.add(surface);
      surface.querySelectorAll('button, a, [role="button"], [onclick]').forEach((item) => candidates.add(item));
    });

    document.querySelectorAll(
      '[id*="joy" i], [class*="joy" i], [id*="avada" i], [class*="avada" i], [aria-label*="reward" i], [title*="reward" i], [aria-label*="loyalty" i], [title*="loyalty" i]'
    ).forEach((element) => {
      if (!(element instanceof Element)) return;
      const clickable = element.matches('button, a, [role="button"], [onclick]')
        ? element
        : element.closest('button, a, [role="button"], [onclick]') || element;
      candidates.add(clickable);
    });

    return Array.from(candidates);
  };

  const findNativeLauncher = () => {
    const matches = [];

    candidateElements().forEach((element) => {
      if (!(element instanceof HTMLElement) || root.contains(element)) return;
      if (!JOY_SIGNAL.test(elementSignal(element))) return;

      const rect = element.getBoundingClientRect();
      if (!isLauncherSized(rect)) return;
      if (!isNearBottomRight(rect) && !isFloatingOnRightEdge(element, rect)) return;
      if (!hasFixedAncestor(element)) return;

      const computed = window.getComputedStyle(element);
      if (computed.display === 'none') return;

      matches.push({
        element,
        area: rect.width * rect.height,
        clickable: element.matches('button, a, [role="button"], [onclick]') ? 1 : 0
      });
    });

    matches.sort((a, b) => {
      if (b.clickable !== a.clickable) return b.clickable - a.clickable;
      return b.area - a.area;
    });

    return matches.length ? matches[0].element : null;
  };

  const hideOnlyNativeLauncher = () => {
    restoreJoySurface();

    const launcher = findNativeLauncher();
    if (!launcher) return null;

    if (keepNativeJoy()) {
      showNativeLauncher(launcher);
      return launcher;
    }

    launcher.dataset.partnersJoyNativeLauncher = 'true';
    launcher.style.setProperty('opacity', '0', 'important');
    launcher.style.setProperty('pointer-events', 'none', 'important');
    launcher.style.setProperty('user-select', 'none', 'important');
    launcher.setAttribute('aria-hidden', 'true');
    launcher.setAttribute('tabindex', '-1');

    return launcher;
  };

  const scheduleLauncherCleanup = () => {
    if (observerFrame) return;
    observerFrame = window.requestAnimationFrame(() => {
      observerFrame = 0;
      hideOnlyNativeLauncher();
    });
  };

  const clickNativeLauncher = () => {
    restoreJoySurface();
    const launcher = findNativeLauncher();
    if (!launcher) return false;

    try {
      launcher.click();
      window.setTimeout(hideOnlyNativeLauncher, 50);
      return true;
    } catch (error) {
      console.warn('Partners Rewards: native Joy launcher could not be clicked.', error);
      return false;
    }
  };

  const openJoyDrawer = () => {
    closeHelpMenu();
    restoreJoySurface();

    let attempts = 0;
    let opened = false;

    const attemptOpen = () => {
      if (opened) return;
      restoreJoySurface();

      if (typeof window.avadaJoyTrigger === 'function') {
        try {
          window.avadaJoyTrigger();
          opened = true;
          window.setTimeout(hideOnlyNativeLauncher, 100);
          return;
        } catch (error) {
          console.warn('Partners Rewards: Joy drawer is not ready yet.', error);
        }
      }

      if (clickNativeLauncher()) {
        opened = true;
        return;
      }

      attempts += 1;
      if (attempts < 40) window.setTimeout(attemptOpen, 250);
    };

    window.addEventListener('joy:ready', attemptOpen, { once: true });
    attemptOpen();
  };

  window.addEventListener('click', (event) => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const trigger = path.find((item) => item instanceof Element && item.matches('[data-partners-rewards-open]'))
      || (event.target instanceof Element ? event.target.closest('[data-partners-rewards-open]') : null);

    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'partners_help_rewards_click',
      page_location: window.location.href
    });

    openJoyDrawer();
  }, true);

  restoreJoySurface();
  hideOnlyNativeLauncher();

  new MutationObserver(scheduleLauncherCleanup).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden']
  });

  window.addEventListener('load', scheduleLauncherCleanup, { once: true });
})();
